import { Component, Input, OnInit } from '@angular/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BmsjHipsterSharedLibsModule, BmsjHipsterSharedModule } from '../../../../../shared';
import { StudyInstanceModel } from '../../../../../shared/dataset/model/study-instance.model';
import { DatasetModel } from '../../../../../shared/dataset/model/dataset.model';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ObservationVariable } from '../../../../../shared/model/observation-variable.model';
import { AlertService } from '../../../../../shared/alert/alert.service';
import { DerivedVariableService, Variable } from '../../../../shared/services/derived-variable.service';
import { StudyEditorContext } from '../../../../study-editor.context';
import { StudyEditorService } from '../../../../study-editor.service';
import { ModalConfirmComponent } from '../../../../../shared/modal/modal-confirm.component';
import { StudyInstanceItem, StudyInstancesTableSelectorComponent } from '../../../../shared/study-instances-table-selector/study-instances-table-selector.component';
import { DatasetService } from '../../../../../shared/dataset/service/dataset.service';

@Component({
    selector: 'jhi-execulate-calculated-variable-modal',
    templateUrl: './execulate-calculated-variable-modal.component.html',
    styleUrl: './execulate-calculated-variable-modal.component.scss'
})
export class ExeculateCalculatedVariableModalComponent implements OnInit {

    @Input() datasetId: number;
    dataset: DatasetModel;
    variables: any[] = [];
    selectedVariable: ObservationVariable = null;
    selectAll: boolean = true;
    datasetInstances: StudyInstanceItem[] = [];
    multipleVariablesMap: { variableKey: string, variable: Variable }[] = [];
    multipleVariableNames: string[] = [];
    studyHasMultipleVariableInDatasets: boolean;
    inputVariableDatasetMapRequest: { [key: string]: number } = {};
    isExecuting: boolean;

    constructor(private modalService: NgbModal,
                private datasetService: DatasetService,
                private activeModal: NgbActiveModal,
                private studyEditorContext: StudyEditorContext,
                private alertService: AlertService,
                private derivedVariableService: DerivedVariableService,
                private studyEditorService: StudyEditorService,
                private translateService: TranslateService,
    ) {}

    async ngOnInit(): Promise<void> {
        if (this.datasetId) {
            const response = await this.datasetService.getDataset(this.studyEditorContext.studyId, this.datasetId).toPromise();
            this.dataset = response.body;
            if (this.dataset.instances) {
                this.dataset.instances.forEach((instance: StudyInstanceModel) => {
                    this.datasetInstances.push({
                        ...instance,
                        isChecked: true
                    });
                });
            }
            if (this.dataset.variables) {
                this.variables = this.dataset.variables.filter((variable: ObservationVariable) => {
                    return variable.formula !== null;
                });
            }
            if (this.variables.length > 0) {
                this.selectedVariable = this.variables[0];
            } else {
                this.alertService.error('study-editor.study-dataset.execute-calculated-variable.choose-dataset-modal.error.no-calculated-variable');
            }
        }
    }

    cancel() {
        this.activeModal.dismiss();
    }

    doSelectAll() {
        this.datasetInstances.forEach((instance) => {
            instance.isChecked = this.selectAll;
        });
    }

    backToDatasetOptionModal() {
        this.activeModal.dismiss('back');
    }

    async tryExecute() {
        const selectedInstaces = this.datasetInstances.filter((instance) => instance.isChecked).map((instance) => instance.instanceId);
        if (this.selectedVariable && selectedInstaces) {
            const formulaVariableDatasetMap = await this.derivedVariableService.getFormulaVariableDatasetMap(this.studyEditorContext.studyId, this.dataset.datasetId, this.selectedVariable.termId).toPromise();
            this.studyHasMultipleVariableInDatasets = false;
            for (const [key, variable] of Object.entries(formulaVariableDatasetMap)) {
                if (variable.datasets && variable.datasets.length > 1) {
                    this.multipleVariablesMap.push({ variableKey: key, variable: variable });
                    this.multipleVariableNames.push(variable.variableName);
                    this.studyHasMultipleVariableInDatasets = true;
                    this.inputVariableDatasetMapRequest[key] = variable.datasets[0].id;
                } else if (variable.datasets && variable.datasets.length === 1) {
                    this.inputVariableDatasetMapRequest[key] = variable.datasets[0].id;
                }
            }
            if (!this.studyHasMultipleVariableInDatasets) {
                this.doExecute(this.inputVariableDatasetMapRequest);
            }
        }
    }
    proceed() {
        this.doExecute(this.inputVariableDatasetMapRequest);
    }

    doExecute(inputVariableDatasetMapRequest: { [key: string]: number}, overwriteExistingData: boolean = false) {
        this.isExecuting = true;
        const selectedInstaces = this.datasetInstances.filter((instance) => instance.isChecked).map((instance) => instance.instanceId);
        if (this.selectedVariable && selectedInstaces) {
            const request = {
                'variableId': this.selectedVariable.termId,
                'geoLocationIds': selectedInstaces,
                'inputVariableDatasetMap': inputVariableDatasetMapRequest,
                'overwriteExistingData': overwriteExistingData };

            this.derivedVariableService.calculateVariableForSubObservation(this.studyEditorContext.studyId, this.dataset.datasetId, request).toPromise().then(
                (response) => {

                    // If "hasDataOverwrite" is true, the derived variable already contains data, and the calculation did not proceed.
                    // The user must confirm whether to overwrite the existing recorded data before continuing.
                    if (!overwriteExistingData && response?.hasDataOverwrite) {
                        this.showConfirmOverwriteData();
                    } else {
                        this.activeModal.close();
                        this.studyEditorService.navigateToDatasetTab(this.studyEditorContext.studyId, this.dataset.datasetId);
                        if (response?.inputMissingData) {
                            this.alertService.warning('warning.custom', { param: response?.inputMissingData });
                        }
                        this.alertService.success('study-editor.study-dataset.execute-calculated-variable.execute-success', { param: this.selectedVariable.name });
                    }
                }
            ).finally(() => {
                this.isExecuting = false;
            });
        }

    }

    isExecuteEnabled(): boolean {
        return !this.isExecuting && this.datasetInstances.some((instance) => instance.isChecked) && this.selectedVariable !== null;
    }

    private showConfirmOverwriteData() {
        const modalConfirmRef = this.modalService.open(ModalConfirmComponent as Component);
        modalConfirmRef.componentInstance.message =  this.translateService.instant('study-editor.study-dataset.execute-calculated-variable.execute-overwrite-data-modal.message');
        modalConfirmRef.componentInstance.cancelLabel = this.translateService.instant('back');
        modalConfirmRef.componentInstance.confirmLabel = this.translateService.instant('proceed');
        modalConfirmRef.componentInstance.showButtonIcons = false;
        modalConfirmRef.result.then(() => {
            this.doExecute(this.inputVariableDatasetMapRequest, true);
        });
    }
}




